import React, { useEffect, useRef } from "react";
import L from "leaflet";
import { STORE_COLORS } from "@/lib/api";
import GoogleMapView from "@/components/GoogleMapView";

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const HOME = [51.0447, -114.0719];

function pin(color, label) {
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};color:#fff;width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 3px 8px rgba(0,0,0,.3);border:2px solid #fff"><span style="transform:rotate(45deg);font-weight:800;font-size:13px">${label}</span></div>`,
    iconSize: [30, 30], iconAnchor: [15, 30],
  });
}

export default function MapView(props) {
  const stores = props.stores || [];
  const home = props.home || HOME;
  const height = props.height || 320;
  if (GMAPS_KEY) return <GoogleMapView apiKey={GMAPS_KEY} stores={stores} home={home} height={height} />;
  const ll = (p) => `${p[0]},${p[1]}`;
  const start = props.homeLabel ? encodeURIComponent(props.homeLabel) : ll(home);
  const stops = [...stores.map((s) => encodeURIComponent(s.address || `${s.lat},${s.lng}`)), start].join("+to:");
  const src = stores.length
    ? `https://maps.google.com/maps?saddr=${start}&daddr=${stops}&dirflg=d&output=embed`
    : `https://maps.google.com/maps?q=${ll(home)}&z=12&output=embed`;
  return (
    <div data-testid="route-map" style={{ height }} className="w-full rounded-2xl overflow-hidden border border-pp-border bg-pp-cream">
      <iframe title="Google Maps route" src={src} className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
    </div>
  );
}

export function LeafletMap({ stores = [], home = HOME, height = 320 }) {
  const ref = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, { scrollWheelZoom: false, zoomControl: true }).setView(home, 11);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors", maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
  }, [home]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.eachLayer((l) => { if (l instanceof L.Marker || l instanceof L.Polyline) map.removeLayer(l); });

    L.marker(home, { icon: pin("#17312A", "H") }).addTo(map).bindPopup("Home");
    const pts = [home];
    stores.forEach((s, i) => {
      const color = STORE_COLORS[s.code] || "#075B3A";
      const ll = [s.lat, s.lng];
      pts.push(ll);
      L.marker(ll, { icon: pin(color, String(i + 1)) }).addTo(map)
        .bindPopup(`<b>${s.name}</b><br/>${s.address || ""}`);
    });
    if (stores.length) pts.push(home);
    if (pts.length > 1) {
      map.fitBounds(L.latLngBounds(pts).pad(0.25));
      const fallback = () => L.polyline(pts, { color: "#075B3A", weight: 3, dashArray: "8 8", opacity: 0.7 }).addTo(map);
      const coords = pts.map((p) => `${p[1]},${p[0]}`).join(";");
      let cancelled = false;
      fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`)
        .then((r) => r.json())
        .then((j) => {
          if (cancelled || mapRef.current !== map) return;
          const g = j.routes?.[0]?.geometry?.coordinates;
          if (!g) return fallback();
          L.polyline(g.map(([lng, lat]) => [lat, lng]), { color: "#075B3A", weight: 5, opacity: 0.85 }).addTo(map);
        })
        .catch(() => { if (!cancelled) fallback(); });
      return () => { cancelled = true; };
    }
    return undefined;
  }, [stores, home]);

  return <div ref={ref} data-testid="route-map" style={{ height }} className="w-full rounded-2xl overflow-hidden border border-pp-border" />;
}
